import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ProfileSetup = () => {
  const navigate = useNavigate();
  const { updateProfile, user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [profileData, setProfileData] = useState({
    nickname: '',
    dailyRoutine: '',
    goals: [],
    // Work
    jobRole: '',
    industry: '',
    workHours: '9-5',
    autoCreateTasks: true,
    memoryMode: 'remember-everything',
    // Life
    billReminder: 'when-due',
    appointmentPreference: 'suggest-first',
    budgetTracking: true,
    habits: [],
    // Health
    weightGoal: '',
    dailyStepTarget: 10000,
    dietType: '',
    takeMedicines: false,
    medicineList: []
  });

  const goalOptions = [
    { id: 'improve-productivity', label: 'Improve productivity' },
    { id: 'get-healthier', label: 'Get healthier' },
    { id: 'manage-life', label: 'Manage my life' },
    { id: 'learn-skills', label: 'Learn new skills' },
    { id: 'reduce-stress', label: 'Reduce stress' }
  ];

  const handleGoalToggle = (goalId) => {
    setProfileData(prev => ({
      ...prev,
      goals: prev.goals.includes(goalId)
        ? prev.goals.filter(g => g !== goalId)
        : [...prev.goals, goalId]
    }));
  };

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      await updateProfile(profileData);
      toast({
        title: "You're all set!",
        description: "I remember how yesterday felt and adjust today."
      });
      navigate('/home');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Step {step} of 4</span>
              <span>{Math.round((step / 4) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-600 to-teal-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 4) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step 1: Personal Preferences */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Personal Preferences</h2>
                <p className="text-gray-600 mt-1">Help us personalize your experience</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nickname">Nickname (How should Allora address you?)</Label>
                  <Input
                    id="nickname"
                    placeholder="e.g., Sarah"
                    value={profileData.nickname}
                    onChange={(e) => setProfileData({ ...profileData, nickname: e.target.value })}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Daily Routine</Label>
                  <Select
                    value={profileData.dailyRoutine}
                    onValueChange={(value) => setProfileData({ ...profileData, dailyRoutine: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select your routine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="early-bird">Early Bird</SelectItem>
                      <SelectItem value="night-owl">Night Owl</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Your Goals</Label>
                  <div className="space-y-2">
                    {goalOptions.map((goal) => (
                      <div key={goal.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={goal.id}
                          checked={profileData.goals.includes(goal.id)}
                          onCheckedChange={() => handleGoalToggle(goal.id)}
                        />
                        <label
                          htmlFor={goal.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {goal.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Work Preferences */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Work Preferences</h2>
                <p className="text-gray-600 mt-1">Customize your work mode</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="jobRole">Job Role</Label>
                  <Input
                    id="jobRole"
                    placeholder="e.g., Product Manager"
                    value={profileData.jobRole}
                    onChange={(e) => setProfileData({ ...profileData, jobRole: e.target.value })}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    placeholder="e.g., Technology"
                    value={profileData.industry}
                    onChange={(e) => setProfileData({ ...profileData, industry: e.target.value })}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Work Hours</Label>
                  <Select
                    value={profileData.workHours}
                    onValueChange={(value) => setProfileData({ ...profileData, workHours: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9-5">9 AM - 5 PM</SelectItem>
                      <SelectItem value="custom">Custom Hours</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="autoCreateTasks"
                    checked={profileData.autoCreateTasks}
                    onCheckedChange={(checked) => setProfileData({ ...profileData, autoCreateTasks: checked })}
                  />
                  <label htmlFor="autoCreateTasks" className="text-sm font-medium cursor-pointer">
                    Auto-create tasks from documents and meetings
                  </label>
                </div>

                <div className="space-y-2">
                  <Label>Work Mode Memory</Label>
                  <Select
                    value={profileData.memoryMode}
                    onValueChange={(value) => setProfileData({ ...profileData, memoryMode: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remember-everything">Remember everything</SelectItem>
                      <SelectItem value="tasks-only">Only remember tasks</SelectItem>
                      <SelectItem value="nothing">Don't remember anything</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Life Preferences */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Life Preferences</h2>
                <p className="text-gray-600 mt-1">Manage your daily life</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Bill Reminder Preference</Label>
                  <Select
                    value={profileData.billReminder}
                    onValueChange={(value) => setProfileData({ ...profileData, billReminder: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="when-due">Only when due</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Appointment Preference</Label>
                  <Select
                    value={profileData.appointmentPreference}
                    onValueChange={(value) => setProfileData({ ...profileData, appointmentPreference: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto-book">Auto-book when possible</SelectItem>
                      <SelectItem value="suggest-first">Suggest first</SelectItem>
                      <SelectItem value="remind-only">Only remind</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="budgetTracking"
                    checked={profileData.budgetTracking}
                    onCheckedChange={(checked) => setProfileData({ ...profileData, budgetTracking: checked })}
                  />
                  <label htmlFor="budgetTracking" className="text-sm font-medium cursor-pointer">
                    Enable budget tracking
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Health Preferences */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Health Preferences</h2>
                <p className="text-gray-600 mt-1">Track your health and wellness</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Weight Goal</Label>
                  <Select
                    value={profileData.weightGoal}
                    onValueChange={(value) => setProfileData({ ...profileData, weightGoal: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select weight goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lose">Lose weight</SelectItem>
                      <SelectItem value="maintain">Maintain weight</SelectItem>
                      <SelectItem value="gain">Gain weight</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stepTarget">Daily Step Target</Label>
                  <Input
                    id="stepTarget"
                    type="number"
                    placeholder="10000"
                    value={profileData.dailyStepTarget}
                    onChange={(e) => setProfileData({ ...profileData, dailyStepTarget: parseInt(e.target.value) })}
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Diet Type</Label>
                  <Select
                    value={profileData.dietType}
                    onValueChange={(value) => setProfileData({ ...profileData, dietType: value })}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select diet type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="vegetarian">Vegetarian</SelectItem>
                      <SelectItem value="vegan">Vegan</SelectItem>
                      <SelectItem value="high-protein">High Protein</SelectItem>
                      <SelectItem value="low-carb">Low Carb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="takeMedicines"
                    checked={profileData.takeMedicines}
                    onCheckedChange={(checked) => setProfileData({ ...profileData, takeMedicines: checked })}
                  />
                  <label htmlFor="takeMedicines" className="text-sm font-medium cursor-pointer">
                    I take daily medicines
                  </label>
                </div>

                {user?.gender === 'female' && (
                  <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <p className="text-sm font-medium text-teal-900">
                      Women's Health features are enabled for you, including cycle tracking and symptom logging.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button
              onClick={handleBack}
              disabled={step === 1 || loading}
              variant="outline"
              className="h-11 px-6"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            {step < 4 ? (
              <Button
                onClick={handleNext}
                className="h-11 px-6 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
              >
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleFinish}
                disabled={loading}
                className="h-11 px-6 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Finish Setup & Enter Allora
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
